import {
  type Lead,
  type LeadStatus,
  Permission,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  cn,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@up2stay/up2promo-ui';
import {
  Contact,
  Mail,
  MessageSquare,
  Phone,
  SquareArrowOutUpRight,
  Trash2,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';

import {
  LEAD_STATUS_IDS,
  leadStatusAccent,
  leadStatusBadgeClass,
} from '@/core/components/leads/lead-status-colors';
import { shellAwarePopoverCollision } from '@/core/hooks/use-css-keyboard-inset';
import { useLongPressMenu } from '@/core/hooks/use-long-press-menu';
import { usePermissions } from '@/core/hooks/use-permissions';
import { canMutateLeadRecord } from '@/core/lib/lead-mutate-access';
import {
  triggerDragStartHaptic,
  triggerErrorHaptic,
  triggerSuccessHaptic,
  triggerTapHaptic,
} from '@/core/lib/dnd-haptics';
import { leadHasVcfContent } from '@/core/lib/leads-vcf';
import { saveLeadToDevice } from '@/core/lib/save-lead-to-device';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

const ICON_ACTION_CLASS =
  'flex min-h-10 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-md px-1 text-[10px] font-semibold leading-tight text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const ROW_ITEM_CLASS =
  'h-9 min-h-9 w-full justify-start gap-2 rounded-md px-2 py-0 text-[13px] font-medium';

const STATUS_CHIP_CLASS =
  'flex h-8 min-h-8 items-center gap-1.5 rounded-md px-2 text-left text-[12px] font-medium leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

type LeadCardQuickMenuProps = {
  lead: Lead;
  children: (state: { highlighted: boolean }) => ReactNode;
  onOpenLead: () => void;
  onSetStatus: (status: LeadStatus) => void;
  onDelete: () => void;
};

function compactPhone(value: string): string {
  return value.replace(/[^\d+]/g, '');
}

export function LeadCardQuickMenu({
  lead,
  children,
  onOpenLead,
  onSetStatus,
  onDelete,
}: LeadCardQuickMenuProps) {
  const dict = useDictionary();
  const { can } = usePermissions();
  const user = useAuthStore((state) => state.user);
  const currentWorkspace = useCurrentWorkspace();
  const canUpdate = canMutateLeadRecord({
    lead,
    viewerUserId: user?.id,
    canUpdate: can(Permission.LEADS_UPDATE),
    canUpdateOthers: can(Permission.LEADS_UPDATE_OTHERS),
    workspaceLeadsAlwaysShared:
      currentWorkspace?.leadsAlwaysSharedWithWorkspace === true,
  });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collisionPadding, setCollisionPadding] = useState({
    top: 12,
    right: 12,
    bottom: 12,
    left: 12,
  });
  const quickDict = dict.leads.list.quickActions;
  const createDict = dict.leads.create;
  const detailDict = dict.leads.detail;
  const saveLabels = detailDict.saveToDevice;

  const phone = lead.phone?.trim() ?? '';
  const email = lead.email?.trim() ?? '';
  const phoneHref = phone ? compactPhone(phone) : '';
  const hasContactActions = Boolean(phoneHref || email);
  const canSave = leadHasVcfContent(lead);

  const menu = useLongPressMenu(() => {
    void triggerDragStartHaptic();
    setCollisionPadding(shellAwarePopoverCollision());
    setOpen(true);
  });

  const close = () => setOpen(false);
  const highlighted = open || menu.isHolding;

  const handleSave = async () => {
    if (saving || !canSave) {
      if (!canSave) toast.error(saveLabels.toastEmpty);
      return;
    }
    void triggerTapHaptic();
    setSaving(true);
    try {
      const result = await saveLeadToDevice(lead);
      if (result.outcome === 'empty') {
        void triggerErrorHaptic();
        toast.error(saveLabels.toastEmpty);
        return;
      }
      if (result.outcome === 'contacts') {
        void triggerSuccessHaptic();
      }
      close();
    } catch (error) {
      console.error('Save lead to device failed', error);
      void triggerErrorHaptic();
      toast.error(saveLabels.toastFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          onContextMenu={menu.onContextMenu}
          onPointerDown={menu.onPointerDown}
          onPointerMove={menu.onPointerMove}
          onPointerUp={menu.onPointerUp}
          onPointerCancel={menu.onPointerCancel}
          onClick={() => {
            if (menu.consumeClickIfMenuOpened()) return;
            onOpenLead();
          }}
        >
          {children({ highlighted })}
        </div>
      </PopoverAnchor>
      <PopoverContent
        role="menu"
        aria-label={quickDict.menuLabel}
        side="bottom"
        align="start"
        sideOffset={8}
        sticky="always"
        collisionPadding={collisionPadding}
        className="z-[10050] w-[min(17.5rem,calc(100vw-1.5rem))] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overflow-x-hidden p-1.5"
      >
        {hasContactActions ? (
          <div className="flex gap-0.5">
            {phoneHref ? (
              <a
                href={`tel:${phoneHref}`}
                role="menuitem"
                className={ICON_ACTION_CLASS}
                onClick={close}
              >
                <Phone className="h-4 w-4" aria-hidden />
                {detailDict.call}
              </a>
            ) : null}
            {email ? (
              <a
                href={`mailto:${email}`}
                role="menuitem"
                className={ICON_ACTION_CLASS}
                onClick={close}
              >
                <Mail className="h-4 w-4" aria-hidden />
                {detailDict.email}
              </a>
            ) : null}
            {phoneHref ? (
              <a
                href={`sms:${phoneHref}`}
                role="menuitem"
                className={ICON_ACTION_CLASS}
                onClick={close}
              >
                <MessageSquare className="h-4 w-4" aria-hidden />
                {quickDict.sms}
              </a>
            ) : null}
          </div>
        ) : null}

        {canUpdate ? (
          <>
            <div
              role="group"
              aria-label={quickDict.setStatus}
              className={cn(
                'grid grid-cols-2 gap-1',
                hasContactActions && 'mt-1.5',
              )}
            >
              {LEAD_STATUS_IDS.map((status) => {
                const current = lead.status === status;
                return (
                  <button
                    key={status}
                    type="button"
                    role="menuitemradio"
                    aria-checked={current}
                    disabled={current}
                    className={cn(
                      STATUS_CHIP_CLASS,
                      current
                        ? cn(leadStatusBadgeClass(status), 'opacity-100')
                        : 'text-foreground hover:bg-accent',
                    )}
                    onClick={() => {
                      close();
                      onSetStatus(status);
                    }}
                  >
                    <span
                      className="size-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: leadStatusAccent(status) }}
                      aria-hidden
                    />
                    <span className="truncate">
                      {dict.leads.status[
                        status as keyof typeof dict.leads.status
                      ] || status}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mx-1 my-1.5 h-px bg-border" />
          </>
        ) : hasContactActions ? (
          <div className="mx-1 my-1.5 h-px bg-border" />
        ) : null}

        <Button
          type="button"
          variant="ghost"
          role="menuitem"
          disabled={saving || !canSave}
          className={ROW_ITEM_CLASS}
          onClick={() => {
            void handleSave();
          }}
        >
          <Contact className="h-3.5 w-3.5" aria-hidden />
          {quickDict.save}
        </Button>
        <Button
          type="button"
          variant="ghost"
          role="menuitem"
          className={ROW_ITEM_CLASS}
          onClick={() => {
            close();
            onOpenLead();
          }}
        >
          <SquareArrowOutUpRight className="h-3.5 w-3.5" aria-hidden />
          {createDict.openDetails}
        </Button>
        {canUpdate ? (
          <Button
            type="button"
            variant="ghost"
            role="menuitem"
            className={cn(
              ROW_ITEM_CLASS,
              'text-destructive hover:bg-destructive/10 hover:text-destructive',
            )}
            onClick={() => {
              close();
              onDelete();
            }}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            {detailDict.delete}
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
